import type { PanelDimensions } from '../../../lib/filters.hooks';
import type { ScopeOption } from '../../filters/types';
import type { MediaKpi, TopMarkets } from './types';

export const DEFAULT_KPI_META: MediaKpi = {
  id: 'video_completion_rate',
  label: 'Video Completion Rate',
  higherIsBetter: true,
  unit: 'percent',
};

export const MEDIA_KPIS: MediaKpi[] = [
  DEFAULT_KPI_META,
  { id: 'impressions', label: 'Impressions', higherIsBetter: true, unit: 'count' },
  { id: 'video_view_rate', label: 'Video View Rate', higherIsBetter: true, unit: 'percent' },
  { id: 'cpm', label: 'CPM', higherIsBetter: false, unit: 'currency' },
  { id: 'ctr', label: 'CTR', higherIsBetter: true, unit: 'percent' },
];

export const DEFAULT_KPI = DEFAULT_KPI_META.id;

export const MEDIA_PANEL_KEYS: PanelDimensions = ['country', 'creativeType', 'campaignObjective'];

export const TOP_MARKET_OPTIONS = [10, 20, 30, 40] as const satisfies readonly TopMarkets[];
export const DEFAULT_TOP_MARKETS: TopMarkets = 20;

export const TOP_MARKET_CHOICES: ScopeOption[] = TOP_MARKET_OPTIONS.map((option) => ({
  id: String(option),
  label: `Top ${option}`,
}));

export function toTopMarkets(value: number | undefined): TopMarkets {
  return TOP_MARKET_OPTIONS.find((option) => option === value) ?? DEFAULT_TOP_MARKETS;
}

export const ACTION_TIP =
  'Review priority based on potential savings as a share of your total spend. High above 7%, Medium above 5%, Low 5% or less.';
export const SAVINGS_TIP =
  'What you could save by pausing or cutting creatives that fall below their performance benchmark.';
export const UPLIFT_TIP =
  'The estimated increase in return you could gain by applying our recommended changes to your creative assets.';

export const OVER_SPEND_TIP =
  'The total budget spent on top-performing creatives and its share of your total spend. The percentage shows how this spend has changed compared to the previous period.';
export const OVER_UPLIFT_TIP =
  'How much better your top-performing creatives are performing compared to your account average. The percentage shows how this spend has changed compared to the previous period.';
export const OVER_AVG_TIP =
  'The average performance of your top-performing creatives. The percentage shows how this performance has changed compared to the previous period.';
export const UNDER_SPEND_TIP =
  'The total budget spent on under-performing creatives and its share of your total spend. The percentage shows how this spend has changed compared to the previous period.';
export const UNDER_UPLIFT_TIP =
  'The potential performance gained by bringing underperforming creatives up to your account average. The percentage shows how this has changed compared to the previous period.';
export const UNDER_AVG_TIP =
  'The average performance of your under-performing creatives. The percentage shows how this performance has changed compared to the previous period.';
export const FATIGUE_SAVINGS_TIP =
  'Budget spent on creatives that fatigued this period, including recovered ones, that you can save by refreshing or pausing them.';

export const ACTION_TONE = {
  high: { fg: 'error.dark', bg: 'score.low.bg' },
  medium: { fg: 'warning.main', bg: 'score.medium.bg' },
  low: { fg: 'success.dark', bg: 'score.high.bg' },
} as const;
